import React, { useEffect } from "react";
import styled from "styled-components";
import { COLORS, Beacon } from "../types";
import { cellToLatLng } from "h3-js";
import { Polyline, Circle, Tooltip, useMap } from "react-leaflet";

const MeasurementPanel = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background-color: rgba(25, 25, 25, 0.8);
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  z-index: 1000;
  max-width: 300px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const StatusIndicator = styled.div<{ $status: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => {
    switch (props.$status) {
      case 'connected': return COLORS.green;
      case 'connecting': return COLORS.orange;
      case 'failed': return COLORS.pink;
      default: return COLORS.grey;
    }
  }};
`;

const Button = styled.button`
  padding: 6px 12px;
  background-color: ${COLORS.blue};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
  font-size: 0.9em;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface MeasureProps {
  myBeacon: Beacon;
  targetBeacon: Beacon;
  status: string;
  rtt: number | null;
  lowestRtt: number | null;
  peerId: string | null;
  onClose: () => void;
}

const Measure: React.FC<MeasureProps> = ({ myBeacon, targetBeacon, onClose, status, rtt, lowestRtt }) => {
  const map = useMap();

  // Adjust map view when lowest RTT changes
  useEffect(() => {
    if (lowestRtt && myBeacon.h3Tile) {
      // Calculate the radius in km from the lowest RTT
      const lowestRttDistance = lowestRtt / 1000 * 2.99e8 / 2000; // km
      const myPos = cellToLatLng(myBeacon.h3Tile);

      // Set the map view to ensure the circle takes up about half the screen
      // Using fitBounds with a calculated radius
      const radiusInMeters = lowestRttDistance * 1000; // Convert to meters

      // Calculate bounds that would make the circle fill about half the screen
      // The coefficient here controls how much of the screen is filled (0.01 = smaller view, 0.1 = larger view)
      const zoomCoefficient = 0.01; // Adjusted to make the circle fill approximately half the screen
      const southWest: [number, number] = [
        myPos[0] - zoomCoefficient * radiusInMeters / 1000,
        myPos[1] - zoomCoefficient * radiusInMeters / 1000
      ];
      const northEast: [number, number] = [
        myPos[0] + zoomCoefficient * radiusInMeters / 1000,
        myPos[1] + zoomCoefficient * radiusInMeters / 1000
      ];

      // Fit the map to these bounds
      map.fitBounds([southWest, northEast]);
    }
  }, [lowestRtt, myBeacon.h3Tile, map]);

  // Render the visual connection (line and circles)
  const renderConnection = () => {
    if (!myBeacon.h3Tile || !targetBeacon.h3Tile) return null;

    try {
      const myPos = cellToLatLng(myBeacon.h3Tile);
      const targetPos = cellToLatLng(targetBeacon.h3Tile);

      const R = 6371; // Earth radius in km
      const dLat = (targetPos[0] - myPos[0]) * Math.PI / 180;
      const dLon = (targetPos[1] - myPos[1]) * Math.PI / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(myPos[0] * Math.PI / 180) * Math.cos(targetPos[0] * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const calculatedDistance = R * c;

      // very simple model: we assume messages travel at 1/2 c and that there is 2 ms of internal latency in the round trip.

      const adjustedRttSeconds = rtt ? Math.max(0, (rtt - 2) / 1000) : null;
      const adjustedLowestRttSeconds = lowestRtt ? Math.max(0, (lowestRtt - 2) / 1000) : null;

      const rttMeters = adjustedRttSeconds ? (adjustedRttSeconds * (2.99e8 / 2) / 2) : null; // Light speed: ~300,000 km/s
      const lowestRttMeters = adjustedLowestRttSeconds ? (adjustedLowestRttSeconds * (2.99e8 / 2) / 2) : null; // Lowest RTT distance

      return (
        <>
          <Polyline
            positions={[myPos, targetPos]}
            pathOptions={{ color: COLORS.pink, weight: 2, dashArray: '5, 5' }}
          >
            <Tooltip permanent>
              {`  ${calculatedDistance.toFixed(2)} km (claimed)`}
              <br></br>
              {rttMeters !== null && `< ${(rttMeters / 1000).toFixed(2)} km (measured via RTT)`}
            </Tooltip>
          </Polyline>
          <Circle
            center={myPos}
            radius={50}
            pathOptions={{ color: COLORS.green, fillColor: COLORS.green, fillOpacity: 0.5 }}
          />
          <Circle
            center={targetPos}
            radius={50}
            pathOptions={{ color: COLORS.blue, fillColor: COLORS.blue, fillOpacity: 0.5 }}
          />
          {lowestRttMeters !== null && (
            <Circle
              center={myPos}
              radius={lowestRttMeters}
              pathOptions={{ color: COLORS.green, fillColor: COLORS.green, fillOpacity: 0.2, weight: 2, dashArray: '5, 5' }}
            >
              <Tooltip permanent>
                {`<`} {(lowestRttMeters / 1000).toFixed(2)} km (lowest RTT)
                <br></br>
                {rttMeters !== null && `< ${(rttMeters / 1000).toFixed(2)} km (last RTT)`}
              </Tooltip>
            </Circle>
          )}
          {rttMeters !== null && (
            <Circle
              center={myPos}
              radius={rttMeters}
              pathOptions={{ color: COLORS.grey, fillOpacity: 0.1, dashArray: '5, 5' }}
            >
            </Circle>
          )}
        </>
      );
    } catch (error) {
      console.error("Error rendering connection:", error);
      return null;
    }
  };

  return (
    <>
      {renderConnection()}
      <MeasurementPanel>
        <h3 style={{ margin: '0 0 10px 0' }}>
          <StatusIndicator $status={status} />
          Connection Status: {status}
        </h3>
        <p><strong>From:</strong> {myBeacon.beaconId.slice(0, 6)}...{myBeacon.beaconId.slice(-4)}</p>
        <p><strong>To:</strong> {targetBeacon.beaconId.slice(0, 6)}...{targetBeacon.beaconId.slice(-4)}</p>
        {status === 'connected' && (
          <div style={{ marginTop: '10px' }}>
            <p style={{ color: COLORS.grey, margin: '0 0 10px 0' }}>
              Last RTT: {rtt ? `${rtt.toFixed(2)} ms` : 'N/A'}
            </p>
            <p style={{ color: COLORS.green, fontWeight: 'bold', margin: '0' }}>
              Lowest RTT: {lowestRtt ? `${lowestRtt.toFixed(2)} ms` : 'N/A'}
            </p>
          </div>
        )}
        {status === 'failed' && (
          <p style={{ color: COLORS.pink }}>
            Connection failed. The target beacon may be offline.
          </p>
        )}
        <Button onClick={() => { onClose(); }} style={{ backgroundColor: COLORS.grey }}>
          Close
        </Button>
      </MeasurementPanel>
    </>
  );
};

export default Measure;